import { useQuery } from '@tanstack/react-query';
import { CreditMemoDataRow } from '@blisspointmedia/bpm-types/dist/CreditMemos';
import {
  awaitJSON,
  JavaLambdaFetch,
  LinearLambdaFetch,
  pollS3,
  UsersLambdaFetch
} from '@src/utils/fetch-utils';
import { CreditMemoRow, CreditMemoStatusType } from './credit-memos-utils';

const CREDIT_MEMO_PDF_BUCKET = 'credit-memo-pdfs';
const LEGACY_CREDIT_MEMO_PDF_BUCKET = 'bpm-cache';

// The PDF generation lambda keyed its output on the concatenation below before credit memos had
// their own id-only bucket -- some older PDFs only exist under this legacy key.
const getLegacyCreditMemoId = (row: CreditMemoRow): string =>
  [row.company, row.week, row.network, row.avail, row.id].join('___'); // 3 underscores

// Exported so callers can key their own queryClient.setQueryData calls off the exact same cache
// entry after a mutation, instead of only patching local state (see CreditMemos.tsx).
export const creditMemosQueryKey = (startWeek: string, endWeek: string) =>
  ['creditMemos', startWeek, endWeek] as const;

export const useCreditMemos = (
  startWeek: string,
  endWeek: string
): ReturnType<typeof useQuery<CreditMemoRow[]>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: creditMemosQueryKey(startWeek, endWeek),
    queryFn: async (): Promise<CreditMemoRow[]> => {
      const res = await LinearLambdaFetch('/credit_memo', {
        method: 'GET',
        params: { startWeek, endWeek }
      });
      const rows = await awaitJSON<CreditMemoDataRow[]>(res);
      return rows.map(row => ({ ...row, selected: false }));
    }
  });

export const deleteCreditMemo = async (id: number): Promise<void> => {
  await LinearLambdaFetch('/credit_memo', {
    method: 'DELETE',
    params: { id }
  });
};

export const patchCreditMemoStatus = async (
  id: number,
  status: CreditMemoStatusType
): Promise<void> => {
  await LinearLambdaFetch('/credit_memo', {
    method: 'PATCH',
    body: { id, status }
  });
};

interface ResolvedCreditMemoPdf {
  bucket: string;
  filename: string;
}

// Tries the current id-only bucket/key first, falling back to the legacy bucket/key for PDFs
// generated before credit memos had their own bucket. Returns whichever actually resolved so
// callers (download, preview, email attachment) all agree on the real location instead of each
// guessing independently.
const resolveCreditMemoPdf = async (
  row: CreditMemoRow,
  { autoDownload }: { autoDownload: boolean }
): Promise<{ contents: Blob; location: ResolvedCreditMemoPdf }> => {
  // Vite statically replaces `process.env` with `{}` at build time (see vite.config.js's
  // `define` block), so a CRA-style `process.env.REACT_APP_STAGE` check is always undefined --
  // `import.meta.env.PROD` is the real production flag here.
  if (!import.meta.env.PROD) {
    const filename = `${row.id}-dev.pdf`;
    const contents = await pollS3({
      bucket: CREDIT_MEMO_PDF_BUCKET,
      mimeType: 'application/pdf',
      filename,
      autoDownload
    });
    return { contents, location: { bucket: CREDIT_MEMO_PDF_BUCKET, filename } };
  }
  try {
    const filename = `${row.id}.pdf`;
    const contents = await pollS3({
      bucket: CREDIT_MEMO_PDF_BUCKET,
      mimeType: 'application/pdf',
      filename,
      maxAttempts: 2,
      autoDownload
    });
    return { contents, location: { bucket: CREDIT_MEMO_PDF_BUCKET, filename } };
  } catch {
    // Fall back to the legacy bucket if it's not in the new one.
    const filename = `${getLegacyCreditMemoId(row)}.pdf`;
    const contents = await pollS3({
      bucket: LEGACY_CREDIT_MEMO_PDF_BUCKET,
      mimeType: 'application/pdf',
      filename,
      autoDownload
    });
    return { contents, location: { bucket: LEGACY_CREDIT_MEMO_PDF_BUCKET, filename } };
  }
};

// Downloads the credit memo PDF from S3 -- pollS3's default autoDownload triggers the browser
// save, so there's nothing further to do with the returned blob here.
export const downloadCreditMemoPDF = async (row: CreditMemoRow): Promise<void> => {
  await resolveCreditMemoPdf(row, { autoDownload: true });
};

export interface CreditMemoMailInfo {
  to: string;
  cc: string[];
  readOnlyCC: string[];
  agency: string;
  bodyText: string;
  subject: string;
}

// The pre-filled mail info (to/cc/subject/body) for a credit memo, plus the current user's email
// appended as a read-only CC so they're always on the thread.
export const useCreditMemoMailInfo = (
  id: number,
  selectedIds: number[]
): ReturnType<typeof useQuery<CreditMemoMailInfo>> =>
  useQuery({
    queryKey: ['creditMemoMailInfo', id],
    queryFn: async (): Promise<CreditMemoMailInfo> => {
      const [creditInformation, userInfo] = await Promise.all([
        awaitJSON<{
          to: string;
          cc: string[];
          readOnlyCC: string | string[];
          agency: string;
          body: string;
          subject: string;
        }>(
          await LinearLambdaFetch('/credit_information', {
            method: 'POST',
            body: { id, selectedIds }
          })
        ),
        awaitJSON<{ email: string }>(await UsersLambdaFetch('/me', { retries: 0 }))
      ]);
      const existingReadOnly = Array.isArray(creditInformation.readOnlyCC)
        ? creditInformation.readOnlyCC
        : [creditInformation.readOnlyCC];
      return {
        to: creditInformation.to,
        cc: creditInformation.cc,
        readOnlyCC: [...existingReadOnly, userInfo.email],
        agency: creditInformation.agency,
        bodyText: creditInformation.body,
        subject: creditInformation.subject
      };
    }
  });

export interface CreditMemoPdfData {
  // Raw PDF blob -- callers derive their own object URL (and revoke it) rather than one being
  // minted here, since a URL created inside a cached queryFn has no owner to ever revoke it.
  contents: Blob;
  // s3://<bucket>/<filename> for whichever bucket the PDF actually resolved from, so the
  // send-email attachment points at the same file the user just previewed.
  attachment: string;
}

// The rendered credit memo PDF, for the send-modal's preview pane.
export const useCreditMemoPdfData = (
  row: CreditMemoRow
): ReturnType<typeof useQuery<CreditMemoPdfData>> =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['creditMemoPdfPreview', row.id],
    queryFn: async (): Promise<CreditMemoPdfData> => {
      const { contents, location } = await resolveCreditMemoPdf(row, { autoDownload: false });
      return {
        contents,
        attachment: `s3://${location.bucket}/${location.filename}`
      };
    }
  });

export interface SendCreditMemoEmailParams {
  to: string;
  cc: string[];
  agency: string;
  subject: string;
  body: string;
  attachment: string;
}

export const sendCreditMemoEmail = async ({
  to,
  cc,
  agency,
  subject,
  body,
  attachment
}: SendCreditMemoEmailParams): Promise<void> => {
  await JavaLambdaFetch('/send_mail', {
    method: 'POST',
    body: {
      to,
      agency,
      cc: cc.join(', '),
      subject,
      body,
      attachment
    }
  });
};
